import React, { type ReactNode, useEffect, useState } from 'react';
import { Provider as StoreProvider } from 'react-redux';

import PersistLoading from 'components/PersistLoading';
import type { RootStore } from 'store';

import { initializeAppStore } from './initializeAppStore';

type StoreStatus = { status: 'loading' } | { status: 'ready'; store: RootStore } | { status: 'failed'; error: unknown };

/**
 * Shows the loading screen until the app store is ready, then provides it. A failed bootstrap
 * throws to the nearest error boundary instead of leaving the loading screen up.
 */
export function AppStoreProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [state, setState] = useState<StoreStatus>({ status: 'loading' });

  useEffect(() => {
    let mounted = true;
    initializeAppStore()
      .then((store) => mounted && setState({ status: 'ready', store }))
      .catch((error: unknown) => mounted && setState({ status: 'failed', error }));
    return () => {
      mounted = false;
    };
  }, []);

  if (state.status === 'failed') throw state.error;
  if (state.status === 'loading') return <PersistLoading />;
  return <StoreProvider store={state.store}>{children}</StoreProvider>;
}
